// src/components/email/EmailLayout.tsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Create, Refresh } from "@mui/icons-material";
import { useEmailContext } from "../../../contexts/EmailContext";
import EmailSidebar from "./EmailSidebar";
import EmailList from "./EmailList";
import EmailThread from "./EmailThread";
import ReplyForm from "./ReplyForm";
import NewMessageModal from "./NewMessageModal";
import { ImapEmail } from "../../../types/imapEmail";
import { useEmailService } from "../../../services/api/emailService";

const EmailLayout: React.FC = () => {
  const { emailHierarchy, threads, loading, error, sendReply, refresh } = useEmailContext();
  const { triggerEmailFetch } = useEmailService();
  const [refreshing, setRefreshing] = useState(false);

  // UI state
  const [selectedThread, setSelectedThread] = useState<ImapEmail[] | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string>("inbox");
  const [selectedAccountType, setSelectedAccountType] = useState<string>("admin");
  const [searchTerm] = useState<string>("");

  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const [showEmailList, setShowEmailList] = useState<boolean>(true);
  const [showReplyModal, setShowReplyModal] = useState<boolean>(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState<boolean>(false);

  // If user hasn't picked an account/folder, pick a default
  useEffect(() => {
    const accountTypes = Object.keys(emailHierarchy);
    if (!selectedAccountType && accountTypes.length > 0) {
      const defaultAccount = accountTypes[0];
      setSelectedAccountType(defaultAccount);
      const folders = Object.keys(emailHierarchy[defaultAccount]);
      if (folders.length > 0) {
        setSelectedFolder(folders[0].toLowerCase());
      }
    }
  }, [emailHierarchy, selectedAccountType]);

  // Filter threads to only those that exist in the selected folder
  const filteredThreads = useMemo(() => {
    if (!selectedFolder) return threads;
    return threads.filter((thread) =>
      thread.some((email) => email.folder.toLowerCase() === selectedFolder)
    );
  }, [threads, selectedFolder]);

  const handleFolderSelect = (accountType: string, folder: string) => {
    setSelectedAccountType(accountType);
    setSelectedFolder(folder.toLowerCase());
    setSelectedThread(null);
  };

  const handleThreadSelect = (thread: ImapEmail[]) => {
    setSelectedThread(thread);
  };

  /**
   * Actually send the reply by calling our context's 'sendReply'
   * The `ReplyForm` passes in `fromEmail, recipient, subject, text, etc.`
   * We rename `text` -> `message` to match the /emails/send payload structure.
   */
  const handleSendReply = async (replyData: {
    fromEmail: string;
    subject: string;
    text: string; // The typed reply text from the ReplyForm
    recipient: string;
    originalEmailId: string;
    recipientName?: string;
    signature?: string;
    ctaUrl?: string;
    ctaLabel?: string;
  }) => {
   ////console.log("Reply data:", replyData);
    try {
      const result = await sendReply({
        fromEmail: replyData.fromEmail,
        recipient: replyData.recipient,
        subject: replyData.subject,
        message: replyData.text, // rename "text" -> "message"
        recipientName: replyData.recipientName,
        signature: replyData.signature,
        ctaUrl: replyData.ctaUrl,
        ctaLabel: replyData.ctaLabel,
        // add additional fields if needed
      });
     ////console.log("Reply send result:", result);
    } catch (err) {
      console.error("Reply sending failed:", err);
    }
    // Hide the modal
    setShowReplyModal(false);
  };

  const handleNewMessageSent = (email: ImapEmail) => {
   ////console.log("New message sent:", email);
    setShowNewMessageModal(false);
  };

  // Handler for manual email fetching
  const handleFetchEmails = async () => {
    setRefreshing(true);
    try {
      // First trigger a direct email fetch
      await triggerEmailFetch();
      
      // Then refresh the UI after a short delay to allow background process to start
      setTimeout(() => {
        refresh(true).then(() => {
          setRefreshing(false);
        }).catch(() => {
          setRefreshing(false);
        });
      }, 1000);
    } catch (error) {
      console.error("Error fetching emails:", error);
      setRefreshing(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "85vh",
        backgroundColor: "background.default",
        position: "relative",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Sidebar */}
          {showSidebar ? (
            <Box
              sx={{
                width: 320,
                position: "relative",
                borderRight: "1px solid",
                borderColor: "divider",
              }}
            >
              <EmailSidebar
                emailHierarchy={emailHierarchy}
                selectedAccountType={selectedAccountType}
                selectedFolder={selectedFolder}
                onFolderSelect={handleFolderSelect}
              />
              <IconButton
                onClick={() => setShowSidebar(false)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "background.paper",
                  boxShadow: 1,
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Box>
          ) : (
            <IconButton
              onClick={() => setShowSidebar(true)}
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: "background.paper",
                boxShadow: 1,
                zIndex: 1000,
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          {/* Email List */}
          {showEmailList ? (
            <Box
              sx={{
                width: 360,
                position: "relative",
                borderRight: "1px solid",
                borderColor: "divider",
              }}
            >
              <EmailList
                threads={filteredThreads}
                onSelectThread={handleThreadSelect}
                searchTerm={searchTerm}
              />
              <IconButton
                onClick={() => setShowEmailList(false)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "background.paper",
                  boxShadow: 1,
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Box>
          ) : (
            <IconButton
              onClick={() => setShowEmailList(true)}
              sx={{
                position: "absolute",
                top: 8,
                left: 340,
                backgroundColor: "background.paper",
                boxShadow: 1,
                zIndex: 1000,
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          {/* Email Detail Section */}
          <Box sx={{ flex: 1, position: "relative" }}>
            {/* Sticky Action Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "background.default",
                zIndex: 2,
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedThread ? (
                <Typography variant="h6" color="text.secondary">
                  Conversation Details
                </Typography>
              ) : (
                <Typography variant="h6" color="text.secondary">
                  Select a conversation to view details.
                </Typography>
              )}
              <Box>
                <Tooltip title="Fetch new emails">
                  <Button
                    variant="outlined"
                    onClick={handleFetchEmails}
                    startIcon={<Refresh />}
                    sx={{ mr: 2 }}
                    disabled={refreshing || loading}
                  >
                    {refreshing ? "Fetching..." : "Fetch Emails"}
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  onClick={() => setShowNewMessageModal(true)}
                  startIcon={<Create />}
                >
                  Compose
                </Button>
              </Box>
            </Box>

            {/* Scrollable Conversation Content */}
            <Box sx={{ p: 2, overflowY: "auto", maxHeight: "calc(100% - 80px)" }}>
              {selectedThread ? <EmailThread thread={selectedThread} /> : null}
            </Box>
          </Box>

          {/* Reply Modal */}
          {showReplyModal && selectedThread && (
            <ReplyForm
              originalEmail={selectedThread[0]}
              onClose={() => setShowReplyModal(false)}
              onSend={handleSendReply} // <--- Actually calls handleSendReply
            />
          )}

          {/* New Message Modal */}
          {showNewMessageModal && (
            <NewMessageModal
              open={showNewMessageModal}
              onClose={() => setShowNewMessageModal(false)}
              onMessageSent={handleNewMessageSent}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default EmailLayout;
